import {useContext, useState} from "react";
import {SignInContext} from "../../../context/SignInContext";
import {Box, Button, ButtonBase, InputBase, Pagination, Skeleton, Stack, Typography} from "@mui/material";
import {Link} from "react-router-dom";
import {HugeiconsIcon} from "@hugeicons/react";
import {Add01Icon, Cancel01Icon, Folder01Icon, Search01Icon} from "@hugeicons/core-free-icons";
import ProjectRichTableRow, {PROJECT_GRID_COLUMNS} from "./project-rich-table-row";
import {Panel} from "../../registry-project/dashboard/components/Panel";

const TYPE_FILTERS = [
  {value: null, label: "All types"},
  {value: "VOICE_ASSISTANT", label: "Voice assistants"},
  {value: "CHATBOT", label: "Chatbots"},
];
const ACCENT = "#8491F7";

// Organization filter pill (dashboard PillSelect colours)
const pillSx = (selected) => ({
  height: {xs: 28, md: 32}, px: {xs: 1.25, md: 1.5}, borderRadius: "999px", fontSize: {xs: 12, md: 13}, fontWeight: 600, whiteSpace: "nowrap", flexShrink: 0,
  color: selected ? "#4B55C9" : "#6B6B80",
  bgcolor: selected ? "rgba(132, 145, 247, 0.14)" : "#fff",
  border: selected ? "1px solid transparent" : "1px solid #E4E4EE",
  transition: "background-color .15s ease, color .15s ease",
  "&:hover": {bgcolor: selected ? "rgba(132, 145, 247, 0.2)" : "#F4F4FA", color: selected ? "#4B55C9" : "#23264D"},
});

const headerCellSx = {fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", color: "#8A8AA0", textTransform: "uppercase"};

// projects: one page of ProjectListDTOs (GET /api/project/paged); paging and the organization / type filters are
// done by the server (filters: {organizationId, type}), the search only narrows the loaded page
export const ProjectRichTableView = ({
  projects, loading, page, pageSize, totalElements, totalPages, onChangePage, filters, onChangeFilters
}) => {
  const {currentUser, organizations} = useContext(SignInContext);
  const [search, setSearch] = useState("");
  // kept as an object so a picked organization the user is not a member of still has a pill with its name
  const [selectedOrganization, setSelectedOrganization] = useState(null);

  const selectOrganization = (organization) => {
    setSelectedOrganization(organization);
    onChangeFilters({organizationId: organization?.id ?? null});
  };

  // project-level manage rights need the member list, so the row menu follows the system role
  const canManage = currentUser?.roles?.some(role => role === "admin" || role === "manager");
  const firstName = currentUser?.firstName || currentUser?.name || "there";

  // from a project's organization chip; the organization may be missing from the pills (e.g. not a member)
  const filterByOrganization = (organizationId, organizationName) => {
    selectOrganization(organizations?.find(o => o?.id === organizationId) || {id: organizationId, name: organizationName});
  };

  // the server sends the page newest first
  const query = search.trim().toLowerCase();
  const pageProjects = query
    ? (projects || []).filter(p => [p?.name, p?.description, ...(p?.subProjects || []).map(lp => lp?.languageName)]
      .some(value => (value || "").toLowerCase().includes(query)))
    : projects || [];
  const filtered = (filters?.organizationId !== null && filters?.organizationId !== undefined) || Boolean(filters?.type);

  // the selected organization may not be one of the user's (picked from a project chip)
  const organizationPills = selectedOrganization && !organizations?.some(o => o?.id === selectedOrganization.id)
    ? [...(organizations || []), selectedOrganization]
    : organizations || [];

  return (
    <Box sx={{flex: 1, minHeight: "100%", bgcolor: "#F7F7FB"}}>
      <Stack gap={{xs: 2, md: 3}} sx={{maxWidth: "xl", mx: "auto", width: "90%", p: {xs: 2, md: 4}, pb: {xs: "60px !important", md: "100px !important"}}}>

        {/* Heading: same structure as the registry DashboardHeading */}
        <Stack direction={{xs: "column", md: "row"}} justifyContent={"space-between"} alignItems={{xs: "stretch", md: "flex-end"}}
               gap={{xs: 1.5, md: 2.5}}>
          <Box sx={{minWidth: 0}}>
            <Stack direction={"row"} alignItems={"center"} gap={1.25}
                   sx={{fontSize: {xs: 11, md: 12}, fontWeight: 600, letterSpacing: {xs: "0.1em", md: "0.14em"}, color: ACCENT}}>
              <Box component={"span"} sx={{width: 22, height: 2, borderRadius: 1, bgcolor: ACCENT}}/>
              YOUR WORKSPACE
            </Stack>
            <Typography component={"h1"} sx={{
              mt: {xs: 0.5, md: 1}, fontSize: {xs: 22, md: 38}, fontWeight: 700, letterSpacing: "-0.03em", lineHeight: 1.1, color: "#23264D",
            }}>
              Welcome back, {firstName}<Box component={"span"} sx={{color: ACCENT}}>.</Box>
            </Typography>
            <Typography sx={{mt: {xs: 0.5, md: 1}, fontSize: {xs: 13, md: 15}, color: "#6B6B80"}}>
              Here&apos;s your project space: every assistant, its languages and where each one stands.
            </Typography>
          </Box>
          <Stack direction={"row"} gap={1.25} alignItems={"center"}>
            <Box data-tour="project-search" sx={{
              height: {xs: 38, md: 42}, flex: 1, width: {md: 320}, display: "flex", alignItems: "center", gap: 1, px: 1.5,
              borderRadius: "12px", border: "1px solid #E4E4EE", bgcolor: "#fff", boxShadow: "0 1px 2px rgba(35, 38, 77, 0.04)",
              "&:hover": {borderColor: "#D6D6E4"},
              "&:focus-within": {borderColor: ACCENT, boxShadow: "0 0 0 3px rgba(132, 145, 247, 0.16)"},
            }}>
              <HugeiconsIcon icon={Search01Icon} size={17} color={"#8A8AA0"}/>
              <InputBase
                fullWidth
                placeholder={"Search this page…"}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{fontSize: {xs: 13, md: 14}, color: "#23264D"}}
              />
              {search && (
                <ButtonBase onClick={() => setSearch("")} aria-label={"Clear search"}
                            sx={{borderRadius: "50%", p: 0.25, color: "#8A8AA0", "&:hover": {color: "#23264D"}}}>
                  <HugeiconsIcon icon={Cancel01Icon} size={14}/>
                </ButtonBase>
              )}
            </Box>
            <Button
              data-tour="create-project"
              component={Link} to={"/registry-project/create"}
              aria-label={"Create project"}
              startIcon={<HugeiconsIcon icon={Add01Icon} size={17}/>}
              sx={{
                height: {xs: 38, md: 42}, minWidth: {xs: 38, md: 64}, px: {xs: 0, md: 2}, borderRadius: "12px", textTransform: "none", fontSize: {xs: 13, md: 14}, fontWeight: 600,
                whiteSpace: "nowrap", flexShrink: 0, color: "#fff", bgcolor: "#23264D", "&:hover": {bgcolor: "#33376B"},
                "& .MuiButton-startIcon": {mr: {xs: 0, md: 1}, ml: {xs: 0, md: -0.5}},
              }}
            >
              <Box component={"span"} sx={{display: {xs: "none", md: "inline"}}}>Create project</Box>
            </Button>
          </Stack>
        </Stack>

        <Stack data-tour="project-filters" direction={"row"} gap={1} alignItems={"center"}
               sx={{overflowX: "auto", pb: 0.5, scrollbarWidth: "none", "&::-webkit-scrollbar": {display: "none"}}}>
          <ButtonBase
            data-tour="filter-all"
            onClick={() => selectOrganization(null)}
            sx={pillSx(selectedOrganization === null)}
          >
            All
          </ButtonBase>
          {organizationPills.map(organization => (
            <ButtonBase
              key={organization?.id}
              onClick={() => selectOrganization(organization)}
              sx={pillSx(organization?.id === selectedOrganization?.id)}
            >
              {organization?.name}
            </ButtonBase>
          ))}
          <Box sx={{display: {xs: "none", md: "block"}, width: "1px", height: 20, bgcolor: "#E4E4EE", mx: 0.5, flexShrink: 0}}/>
          {TYPE_FILTERS.map(item => (
            <ButtonBase
              key={item.label}
              onClick={() => onChangeFilters({type: item.value})}
              sx={pillSx((filters?.type || null) === item.value)}
            >
              {item.label}
            </ButtonBase>
          ))}
        </Stack>

        {/* Projects */}
        <Panel sx={{p: {xs: 2, md: 3}, gap: 2}}>



          <Box data-tour="projects-list">
            {/* Column titles (desktop) */}
            <Box sx={{
              display: {xs: "none", md: "grid"}, gridTemplateColumns: PROJECT_GRID_COLUMNS, gap: 2, px: 2, pb: 1,
              borderBottom: "1px solid #F0F0F6",
            }}>
              <Typography sx={headerCellSx}>Project</Typography>
              <Typography sx={headerCellSx}>Members</Typography>
              <Typography sx={{...headerCellSx, textAlign: "center"}}>Active calls</Typography>
              <Typography sx={headerCellSx}>Stage</Typography>
              <Box/>
            </Box>

            {loading && [0, 1, 2, 3].map(index => (
              <Stack key={index} direction={"row"} alignItems={"center"} gap={1.5}
                     sx={{px: 2, py: 2, mb: 1.5, borderRadius: "14px", bgcolor: "#FAFAFD"}}>
                <Skeleton variant={"rounded"} width={44} height={44} sx={{borderRadius: "12px", flexShrink: 0}}/>
                <Box sx={{flex: 1, minWidth: 0}}>
                  <Skeleton variant={"rounded"} width={"45%"} height={18} sx={{borderRadius: "6px"}}/>
                  <Skeleton variant={"rounded"} width={"30%"} height={14} sx={{borderRadius: "6px", mt: 1}}/>
                </Box>
                <Skeleton variant={"rounded"} width={80} height={24} sx={{borderRadius: "8px", display: {xs: "none", md: "block"}}}/>
              </Stack>
            ))}

            {!loading && pageProjects.length === 0 && (
              <Stack alignItems={"center"} gap={0.75} sx={{py: 6, textAlign: "center"}}>
                <Box sx={{
                  width: 44, height: 44, borderRadius: "14px", display: "flex", alignItems: "center", justifyContent: "center",
                  color: "#4B55C9", bgcolor: "rgba(132, 145, 247, 0.14)", mb: 0.5,
                }}>
                  <HugeiconsIcon icon={query ? Search01Icon : Folder01Icon} size={20}/>
                </Box>
                <Typography sx={{fontSize: 15, fontWeight: 700, color: "#23264D"}}>
                  {query
                    ? "No projects on this page match your search"
                    : filtered ? "No projects match these filters" : "No projects yet"}
                </Typography>
                <Typography sx={{fontSize: 13, color: "#8A8AA0"}}>
                  {query
                    ? "The search only covers the projects on this page."
                    : filtered ? "Try another organization or type." : "Create a project to start building your assistant."}
                </Typography>
              </Stack>
            )}

            {!loading && pageProjects.map(project => (
              <ProjectRichTableRow
                key={project?.id}
                project={project}
                canManage={canManage}
                onFilterOrganization={filterByOrganization}
              />
            ))}
          </Box>

          {totalPages > 1 && (
            <Stack direction={{xs: "column", sm: "row"}} justifyContent={"space-between"} alignItems={"center"} gap={1.5}
                   sx={{pt: 1}}>
              <Typography sx={{fontSize: 12, color: "#8A8AA0"}}>
                Showing {page * pageSize + 1}–{Math.min((page + 1) * pageSize, totalElements)} of {totalElements}
              </Typography>
              <Pagination
                count={totalPages}
                page={page + 1}
                disabled={loading}
                onChange={(event, value) => onChangePage(value - 1)}
                shape={"rounded"}
                size={"small"}
                siblingCount={1}
                boundaryCount={1}
                sx={{
                  "& .MuiPaginationItem-root": {borderRadius: "8px", fontSize: 12, fontWeight: 600, color: "#4A4A63"},
                  "& .Mui-selected": {bgcolor: "rgba(132, 145, 247, 0.14) !important", color: "#4B55C9"},
                }}
              />
            </Stack>
          )}
        </Panel>
      </Stack>
    </Box>
  );
};
